import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Badge,
  Icon,
  useColorModeValue,
  Checkbox,
  Button,
  Center,
} from '@chakra-ui/react';
import { FiClock, FiCalendar, FiArrowRight } from 'react-icons/fi';
import moment from 'moment';

/**
 * My Tasks Widget
 * 
 * Displays upcoming CRM tasks on the dashboard.
 * Shows 5 most urgent tasks with quick complete action.
 */
// Millisecond timestamp for sorting. Handles JS Date (Supabase via useTasks),
// Firestore Timestamp (legacy), and ISO strings. Tasks without a due date sort
// last — they are the least urgent, not the most.
const getDueMillis = (dueDate) => {
  if (!dueDate) return Infinity;
  const date = dueDate.toDate ? dueDate.toDate() : new Date(dueDate);
  const millis = date.getTime();
  return Number.isNaN(millis) ? Infinity : millis;
};

export default function MyTasksWidget({ tasks = [], onTaskComplete, onTaskClick, onViewAll }) {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const activeTasks = tasks.filter(t => t.status !== 'completed' && t.status !== 'cancelled');

  const urgentTasks = [...activeTasks]
    .sort((a, b) => getDueMillis(a.dueDate) - getDueMillis(b.dueDate))
    .slice(0, 5);

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    const due = dueDate.toDate ? dueDate.toDate() : new Date(dueDate);
    return moment(due).isBefore(moment(), 'day');
  };

  return (
    <Box
      bg={bgColor}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      p={5}
      shadow="sm"
      h="full"
      display="flex"
      flexDirection="column"
    >
      <HStack justify="space-between" mb={4}>
        <HStack spacing={2}>
          <Icon as={FiClock} boxSize={5} color="purple.500" />
          <Heading size="sm">My Tasks</Heading>
        </HStack>
        {/* Total open tasks, not just the 5 shown */}
        <Badge colorScheme="purple" fontSize="sm">
          {activeTasks.length}
        </Badge>
      </HStack>

      <VStack align="stretch" spacing={2} flex={1} maxH="400px" overflowY="auto">
        {urgentTasks.length === 0 ? (
          <Center py={6} flexDirection="column">
            <Icon as={FiCalendar} boxSize={10} color="gray.300" mb={2} />
            <Text color="gray.500" fontSize="sm">
              No upcoming tasks
            </Text>
            <Text color="gray.400" fontSize="xs">
              You're all caught up!
            </Text>
          </Center>
        ) : (
          urgentTasks.map((task) => {
            const overdue = isOverdue(task.dueDate);
            
            return (
              <Box
                key={task.id}
                p={3}
                borderRadius="md"
                borderWidth="1px"
                borderColor={overdue ? 'red.300' : borderColor}
                bg={overdue ? 'red.50' : 'transparent'}
                _hover={{ bg: hoverBg, cursor: 'pointer' }}
                transition="all 0.2s"
                onClick={() => onTaskClick && onTaskClick(task)}
              >
                <HStack spacing={3} align="flex-start">
                  <Checkbox
                    isChecked={task.status === 'completed'}
                    onChange={(e) => {
                      e.stopPropagation();
                      onTaskComplete && onTaskComplete(task.id);
                    }}
                    colorScheme="green"
                    mt={1}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <VStack align="flex-start" spacing={0} flex={1}>
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      noOfLines={1}
                    >
                      {task.title}
                    </Text>
                    <HStack spacing={2} fontSize="xs">
                      {task.dueDate && (
                        <Text color={overdue ? 'red.600' : 'gray.600'}>
                          {moment(task.dueDate.toDate ? task.dueDate.toDate() : task.dueDate).format('MMM DD')}
                        </Text>
                      )}
                      {task.dueTime && (
                        <Text color="gray.600">{task.dueTime}</Text>
                      )}
                      {overdue && (
                        <Badge colorScheme="red" fontSize="xs">Overdue</Badge>
                      )}
                    </HStack>
                  </VStack>
                </HStack>
              </Box>
            );
          })
        )}
      </VStack>

      {urgentTasks.length > 0 && onViewAll && (
        <Button
          size="sm"
          variant="ghost"
          rightIcon={<FiArrowRight />}
          onClick={onViewAll}
          mt={3}
        >
          View All Tasks
        </Button>
      )}
    </Box>
  );
}

